<script>
export default {
  name: 'OrderCart'
}
</script>

<script setup>
import { ref, reactive, onMounted, computed, nextTick, resolveComponent } from 'vue';

import Pagination from '@parts/Pagination.vue';
import OrderNavigation from '@parts/OrderNavigation.vue';
import GoodsList from '@parts/GoodsList.vue';
import axios from 'axios';
import DeliverySpecifiedDateWarningModal from './modal/DeliverySpecifiedDateWarningModal.vue';
import DeliverySpecifiedDateUpdateModal from './modal/DeliverySpecifiedDateUpdateModal.vue';
import DeliverySpecifiedDateUpdateCompleteModal from './modal/DeliverySpecifiedDateUpdateCompleteModal.vue';
import BulkDeleteConfirmModal from './modal/BulkDeleteConfirmModal.vue';
import DeleteFromCartCompleteModal from './modal/DeleteFromCartCompleteModal.vue';
import OrderConfirmModal from './modal/OrderConfirmModal.vue';
import OrderErrorModal from './modal/OrderErrorModal.vue';
import OrderCompleteModal from './modal/OrderCompleteModal.vue';
import AddToFavoriteModal from './modal/AddToFavoriteModal.vue';
import AddToFavoriteCompleteModal from './modal/AddToFavoriteCompleteModal.vue';
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import CartReloadRequiredModal from '@parts/CartReloadRequiredModal.vue';
import { modalUtil } from '@functions/modalUtil.js';
import { url } from '@/config/urls';
import router from '@/router/index.js';
import { useRoute } from 'vue-router';

// 外部定義メソッド
const { showModal, switchFromCurrentModal } = modalUtil();
const route = useRoute();

// 画面ID
const screenId = ref("OrderCart");

// 画面モード
const screenMode = ref("default");

// エラーメッセージの状態管理
const errorMessages = ref([]);

// 定数定義
const pageSizeArray = [50, 100, 200];

// Prop定義
const props = defineProps({
  userDataUrl: {
    type: String,
    default: url.api.userData
  },
  orderCartPageUrl: {
    type: String,
    default: url.page.orderCart
  },
  orderCartListApiUrl: {
    type: String,
    default: url.api.orderCart.list
  },
  orderCartCountApiUrl: {
    type: String,
    default: url.api.orderCart.count
  },
  orderCartUpdateApiUrl: {
    type: String,
    default: url.api.orderCart.update
  },
  orderCartDeleteApiUrl: {
    type: String,
    default: url.api.orderCart.delete
  },
  doOrderApiUrl: {
    type: String,
    default: url.api.doOrder
  },
  orderSearchPageUrl: {
    type: String,
    default: url.page.orderSearch
  },
  orderFavoritePageUrl: {
    type: String,
    default: url.page.orderFavorite
  },
  orderFavoriteListApiUrl: {
    type: String,
    default: url.api.orderFavorite.list
  },
  orderFavoriteAddApiUrl: {
    type: String,
    default: url.api.orderFavorite.add
  },
  goodsTypesApiUrl: {
    type: String,
    default: url.api.goodsTypes
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  }
});


// ユーザー情報
const user = reactive({
  isPermittedOrder: null,  // null: 未取得, true: 発注, false: 在庫問合せ
  deliveryDateAllowedFlg: null  // null: 未取得, 1: 納品日指定可能, 0: 納品日指定不可
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedOrder = response.data.response.isPermittedOrder;
    user.deliveryDateAllowedFlg = response.data.response.deliveryDateAllowedFlg;
  } catch (error) {
    apiError(error, errorMessages);
  }
}

// 発注No（発注後にセットされる）
const orderNumber = ref(null);

// 商品一覧取得APIリクエストパラメータ
const condition = ref({
  size: 50,
  page: 1
});

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// お気に入りグループ
const goodsFavorites = ref([]);
const hasFavoriteSlot = ref(false);

// 検索結果
const goodsList = ref([]);

// カート更新日時
const cartUpdatedAt = ref(null);

// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};

// エラーフラグをオブジェクトで管理
const validationErrors = reactive({})

// バリデーションエラーの更新
const validationErrorHandle = (key, value) => {
  validationErrors[key] = value
}

// 全体エラー判定
const hasError = computed(() => {
  return Object.values(validationErrors).some(v => v)
})

// カート商品 品目数（行数）
const totalNum = ref(0);
// カート商品 数量合計
const totalQuantity = ref(0);
// カート商品集計
const countCartGoods = () => {
  return axios.get(props.orderCartCountApiUrl)
    .then((response) => {
      totalNum.value = parseInt(response.data.response.totalNum);
      totalQuantity.value = parseInt(response.data.response.totalQuantity);
      getGoodsList();
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// チェック済みリスト
const checkedList = ref([]);
// 他ページ込チェック済みリスト
const allCheckedList = ref([]);

// チェック済みリスト更新
const handleUpdateCheckedList = (updatedList) => {
  checkedList.value = updatedList;
}
//　他ページ込チェック済みリスト更新
const handleUpdateAllCheckedList = (updatedList) => {
  allCheckedList.value = updatedList;
}

// お気に入りマスタデータ取得処理
const getGoodsFavorites = () => {
  axios.get(props.orderFavoriteListApiUrl)
    .then((response) => {
      goodsFavorites.value = response.data.response.goodsFavorites;
      // TODO：テスト用
      hasFavoriteSlot.value = true;
      // hasFavoriteSlot.value = response.data.response.hasFavoriteSlot;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}


// 商品分類マスタデータ取得処理
const getGoodsTypes = () => {
  axios.get(props.goodsTypesApiUrl)
    .then((response) => {
      goodsTypes.value = response.data.response.goodsTypes;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品一覧取得処理
const getGoodsList = async() => {
  handleUpdateLoading(true);
  try {
    const response = await axios.get(props.orderCartListApiUrl, {
      params: condition.value
    })
    // goodsListが null または undefined の場合は空配列をセット
    goodsList.value = response.data.response.goodsList ?? [];
    totalNum.value = parseInt(response.data.response.totalNum);
    totalQuantity.value = parseInt(response.data.response.totalQuantity);
    pagingParameter.value.pageNum = parseInt(response.data.response.currentPage);
    totalPage.value = Math.ceil(totalNum.value / pagingParameter.value.pageSize);
    totalPage.value = totalPage.value ? totalPage.value : 1;
    // allCheckedListとgoodsListの両方に含まれるjanCodeのみをcheckedListに設定
    checkedList.value = allCheckedList.value.filter(janCode => 
      goodsList.value.map(item => item.janCode).includes(janCode)
    );
    // 最終更新日時を保持
    cartUpdatedAt.value = response.data.response.cartUpdatedAt;
  } catch(error) {
    apiError(error, errorMessages);
  } finally {
    handleUpdateLoading(false);
  }
}

// ページネーション処理
const onChangePageNum = (page) => {
  if (!Number.isFinite(page)) {
    return;
  }
  condition.value.page = page;
  getGoodsList();

  pagingParameter.value.pageNum = page;

  window.scroll({
      top: 0,
      behavior: 'smooth'
  });
}

const onChangePageSize = (selected) => {
  condition.value.page = 1;
  condition.value.size = selected;
  getGoodsList();

  pagingParameter.value.pageNum = 1;
  pagingParameter.value.pageSize = selected;

}

// 検索条件のセット
const setCondition = () => {
  condition.value.size = pagingParameter.value.pageSize || 50;
  condition.value.page = 1;
  pagingParameter.value.pageNum = 1;
}

// 発注カートを再表示
const resetCartState = async() => {
  // 一旦リセット
  screenMode.value = 'default';
  checkedList.value = [];
  allCheckedList.value = [];
  condition.value.page = 1;
  
  await Promise.all([
    getGoodsList(),
    countCartGoods()
  ]);
}

const onDeliveryDateUpdated = async() => {
  await Promise.all([
    getGoodsList()
    , countCartGoods()
  ]);

  switchToDefaultMode();
}

// 発注TOPページへ移動
const goToOrderSearch = () => {
  router.push(props.orderSearchPageUrl);
}

// まとめて削除モードに切り替え
const switchToBulkDeleteMode = () => {
  screenMode.value = 'bulkDelete';
  checkedList.value = [];
  allCheckedList.value = [];
}
// まとめて編集モードに切り替え
const switchToBulkUpdateMode = () => {
  screenMode.value = 'bulkUpdate';
  checkedList.value = [];
  allCheckedList.value = [];
}
// デフォルトモードに切り替え
const switchToDefaultMode = () => {
  screenMode.value = 'default';
  checkedList.value = [];
  allCheckedList.value = [];
}

// カート商品 削除
const deleteFromCart = () => {
  axios.post(props.orderCartDeleteApiUrl, {
    janCodeList: checkedList.value
  })
    .then((response) => {
      handleCartDeleted();
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// カート商品削除後の再取得処理
const handleCartDeleted = async () => {
  // デフォルトモードに戻す
  switchToDefaultMode();

  document.querySelectorAll('.modal.show').forEach(el => {
    bootstrap.Modal.getInstance(el)?.hide();
  });

  document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
  document.body.classList.remove('modal-open');

  try {
    // カートデータを再取得（並列実行）
    await Promise.all([
      getGoodsList(),
      countCartGoods()
    ]);
    
    await nextTick();

    setTimeout(() => {
      showModal('deleteFromCartCompleteModal');
    }, 200);
  } catch (error) {
    router.push(props.error);
  }
}

// 納品指定不可商品が含まれているモーダル「はい」押下時処理
const onClickDeliverySpecifiedDateWarningModalButton = () => {
  switchFromCurrentModal('deliverySpecifiedDateUpdateModal');
}

// 発注時のカート更新チェックで返るエラー文言。
// 発注実行の直前にカートが他端末等で更新されていた場合、サーバーがこの文言で発注を弾く。
// ※ バックエンドの messages.properties の message.cart.optimistic_lock と一致させること
const CART_OPTIMISTIC_LOCK_MESSAGE = 'カート内容が更新されています。再読み込みしてください。';

// 発注が「カートが更新済みのため弾かれた」エラー（400 かつ 上記文言）かどうかを判定
const isCartOptimisticLockError = (error) => {
  if (error?.response?.status !== 400) return false;
  const errors = error?.response?.data?.error;
  if (!Array.isArray(errors)) return false;
  return errors.some(e => e?.message === CART_OPTIMISTIC_LOCK_MESSAGE);
}

// 発注確認モーダル「はい」押下時処理
const doOrder = async() => {
  handleUpdateLoading(true);
  try {
    const response = await axios.post(props.doOrderApiUrl, {
      cartUpdatedAt: cartUpdatedAt.value
    });

    // 発注Noを保持
    orderNumber.value = response.data.response.orderNumber;

    // カートを空＋再取得
    await Promise.all([
      getGoodsList()
      , countCartGoods()
    ]);

    await nextTick();

    // 確認モーダルを閉じる
    bootstrap.Modal.getInstance(
      document.getElementById('orderConfirmModal')
    )?.hide();

    // 発注完了モーダルを表示
    setTimeout(() => {
      showModal('orderCompleteModal');
    }, 200);
  } catch(error) {
      // カートが他端末等で更新済みのため弾かれた場合は、専用モーダルで再読み込みを促す。
      // それ以外のエラーは従来どおり共通モーダルで表示する。
      if (isCartOptimisticLockError(error)) {
        switchFromCurrentModal('cartReloadRequiredModal');
      } else {
        apiError(error, errorMessages);
      }
  } finally {
      handleUpdateLoading(false);
  };
}

// 発注完了モーダル「発注商品をお気に入りグループに登録しますか？」押下時処理
const onClickOrderCompleteModalLink = () => {
  switchFromCurrentModal('addToFavoriteModal');
}

// URlパラメータを使用してモーダルを表示
const showModalWithUrlParam = () => {

  if(route.query.num !== undefined) {
    const num = Number(route.query.num);

    if(!Number.isNaN(num)) {
      orderNumber.value = num;
    }

    // numをURLから削除
    const { num: _num, ...restQuery } = route.query;
    router.replace({ query: restQuery });
  }

  // URLパラメータに応じてモーダル表示
  const modalMap = {
    'deleted': 'deleteFromCartCompleteModal',
    'updated': 'deliverySpecifiedDateUpdateCompleteModal',
  };

  for (const [param, modalId] of Object.entries(modalMap)) {
    if (route.query[param] === 'true') {
      // URLパラメータを削除
      const { [param]: _removed, ...restQuery } = route.query;
      router.replace({ query: restQuery });
      
      // 画面読み込み完了後にモーダル表示
      nextTick(() => {
        setTimeout(() => showModal(modalId), 100);
      });
      break;
    }
  }
}

// 初期処理
onMounted(async () => {
  await getUserInfo();
  if (!user.isPermittedOrder) {
    return;
  }

  await Promise.all([
    getGoodsFavorites(),
    getGoodsTypes(),
    getGoodsList()
  ]);

  showModalWithUrlParam();
});
</script>

<template>

  <div id="contents1" class="bg1" v-if="user.isPermittedOrder">
    <OrderNavigation
      :screenId = "screenId"
      :screenMode = "screenMode"
      :cartGoodsTotalRecord = totalNum
    ></OrderNavigation>
    
    <template v-if="goodsList.length === 0">
      <h2 class="com_title1 com_mgb5">発注カート</h2>
      <p class="com_center1 com_fs16">カート内は空です</p>
      <div class="com_center1 com_mgb20">
        <button type="button" class="btn com_btn2 wd1" @click="goToOrderSearch">発注TOP</button>
      </div>
    </template>
    <template v-else-if="screenMode === 'bulkDelete'">
      <h2 class="com_title1 com_mgb5">発注カート<span class="sub1">(まとめて削除モード)</span></h2>
      <p class="com_center1 com_fs16">カートから削除する商品を選択してください</p>
    </template>
    <template v-else-if="screenMode === 'bulkUpdate'">
      <h2 class="com_title1 com_mgb5">発注カート<span class="sub1">(納品指定日まとめて編集モード)</span></h2>
      <p class="com_center1 com_fs16">納品指定日を変更する商品を選択してください</p>
    </template>
    <template v-else>
      <h2 class="com_title1 com_mgb5">発注カート</h2>
      <p class="com_center1 com_fs16">以下の商品を発注しますか</p>
      <p class="com_center1 com_fs16 com_strong1">
        行数：<span class="com_color1">{{ totalNum }}</span>&emsp;
        数量合計：<span class="com_color1">{{ totalQuantity }}</span></p>
      <div class="com_center1 com_mgb20">
        <button type="button" data-bs-toggle="modal" data-bs-target="#orderConfirmModal" class="btn com_btn1 wd1"
        :disabled="hasError">発注する</button>
      </div>
    </template>

    <div v-if="goodsList.length > 0" class="com_inner4 clearfix">

      <Pagination
        :isShowFirstAndLastBtn="true"
        :isShowPaginationInfo="true"
        :totalNum="totalNum"
        :currentPage="pagingParameter.pageNum"
        :totalPage="totalPage"
        :pageSize="pagingParameter.pageSize"
        :pageSizeSelectList="pageSizeArray"
        @changePageNum="onChangePageNum"
        @changePageSize="onChangePageSize"
      ></Pagination>

      <template v-if="screenMode !== 'bulkDelete' && screenMode !== 'bulkUpdate'">
        <div class="com_flx1 com_mgb10">
          <a @click.prevent="switchToBulkDeleteMode" class="com_btn10"><span>カートから<br>まとめて削除<img src="/front/assets/common/img/icon_trash1.png" alt="" class="icon_trash1"></span></a>
          <a @click.prevent="switchToBulkUpdateMode" class="com_btn10" v-if="user.deliveryDateAllowedFlg === 1"><span>納品指定日<br>まとめて編集<img src="/front/assets/common/img/icon_calender2.png" alt="" class="icon_calender2"></span></a>
        </div>
      </template>

      <GoodsList
        :screenId = "screenId"
        :screenMode = "screenMode"
        :isPermittedOrder = user.isPermittedOrder
        :deliveryDateAllowedFlg = user.deliveryDateAllowedFlg
        :goodsList = goodsList
        :goodsTypes = goodsTypes
        :goodsFavorites = goodsFavorites
        :checkedList = checkedList
        :allCheckedList = allCheckedList
        @updateLoading="handleUpdateLoading"
        @updateCheckedList="handleUpdateCheckedList"
        @updateAllCheckedList="handleUpdateAllCheckedList"
        @countCartGoods="countCartGoods"
        @switchToDefaultMode="switchToDefaultMode"
        @apiError="handleApiError"
        @validationError = "validationErrorHandle"
        @cartDeleted="handleCartDeleted"
        @resetCartState="resetCartState"
      ></GoodsList>

      <Pagination
        :isShowFirstAndLastBtn="true"
        :isShowPaginationInfo="true"
        :totalNum="totalNum"
        :currentPage="pagingParameter.pageNum"
        :totalPage="totalPage"
        :pageSize="pagingParameter.pageSize"
        :pageSizeSelectList="pageSizeArray"
        @changePageNum="onChangePageNum"
        @changePageSize="onChangePageSize"
      ></Pagination>

    </div>
  </div>

  <!-- 納品指定不可商品が含まれているモーダル -->
  <DeliverySpecifiedDateWarningModal
    @onClickDeliverySpecifiedDateWarningModalButton="onClickDeliverySpecifiedDateWarningModalButton"
  ></DeliverySpecifiedDateWarningModal>

  <!-- 納品指定日一括更新モーダル -->
  <DeliverySpecifiedDateUpdateModal
    :goodsList=goodsList
    :checkedList=checkedList
    :allCheckedList=allCheckedList
    :orderCartUpdateApiUrl = props.orderCartUpdateApiUrl
    @updateLoading="handleUpdateLoading"
    @apiError="handleApiError"
    @deliveryDateUpdated="onDeliveryDateUpdated"
  ></DeliverySpecifiedDateUpdateModal>

  <!-- 納品指定日更新完了モーダル -->
  <DeliverySpecifiedDateUpdateCompleteModal></DeliverySpecifiedDateUpdateCompleteModal>

  <!-- まとめて削除確認モーダル -->
  <BulkDeleteConfirmModal
    @updateLoading="handleUpdateLoading"
    @deleteFromCart="deleteFromCart"
  ></BulkDeleteConfirmModal>

  <!-- 削除完了モーダル -->
  <DeleteFromCartCompleteModal></DeleteFromCartCompleteModal>

  <!-- 発注確認モーダル -->
  <OrderConfirmModal
    @doOrder="doOrder"
  ></OrderConfirmModal>

  <!-- 発注エラーモーダル -->
  <OrderErrorModal
    @resetCartState="resetCartState"
  ></OrderErrorModal>

  <!-- 発注完了モーダル -->
  <OrderCompleteModal
    @onClickOrderCompleteModalLink="onClickOrderCompleteModalLink"
  ></OrderCompleteModal>

  <!-- 発注後お気に入り登録モーダル -->
  <AddToFavoriteModal
    :goodsFavorites=goodsFavorites
    :hasFavoriteSlot=hasFavoriteSlot
    :orderNumber=orderNumber
    @updateLoading="handleUpdateLoading"
    @getGoodsFavorites="getGoodsFavorites"
    @apiError="handleApiError"
  ></AddToFavoriteModal>

  <!-- 発注後お気に入り登録完了モーダル -->
  <AddToFavoriteCompleteModal
  ></AddToFavoriteCompleteModal>

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <!-- カートが他端末等で更新されていて発注が弾かれた場合の、再読み込みモーダル -->
  <CartReloadRequiredModal
    @reload="resetCartState"
  ></CartReloadRequiredModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>
